import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import axios from 'libs/Axios';
import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import {
  KpiDefinition,
  KpiInsightViewColumn,
  KpiDefinitionRequest,
  KpiDefinitionSourceColumn,
  UpdateKpiDefinitionRequest,
} from 'types/api/SystemTenant/AROChart/KpiDefinitionTypes';

export const KPI_DEFINITION_QUERY_KEY = {
  LIST: 'KPI_DEFINITION_LIST',
  DETAILS: 'KPI_DEFINITION_DETAILS',
  INSIGHT_VIEW_COLUMNS: 'KPI_DEFINITION_INSIGHT_VIEW_COLUMNS',
  SOURCE_COLUMNS: 'KPI_DEFINITION_SOURCE_COLUMNS',
};

const KPI_DEFINITION_BASE_PATH = '/api/v1/kpi-definition';

export function useGetKpiDefinitions(insightViewDefinitionId?: string, enabled = true) {
  return useQuery<AxiosResponse<KpiDefinition[]>, AxiosDefaultErrorEntity>(
    [KPI_DEFINITION_QUERY_KEY.LIST, insightViewDefinitionId],
    () =>
      axios
        .get(KPI_DEFINITION_BASE_PATH, {
          params: insightViewDefinitionId ? { insightViewDefinitionId } : undefined,
        })
        .then((res) => res),
    { enabled }
  );
}

export function useGetKpiDefinition(id?: string) {
  return useQuery<AxiosResponse<KpiDefinition>, AxiosDefaultErrorEntity>(
    [KPI_DEFINITION_QUERY_KEY.DETAILS, id],
    () => axios.get(`${KPI_DEFINITION_BASE_PATH}/${id}`).then((res) => res),
    { enabled: Boolean(id) }
  );
}

export function useGetKpiDefinitionSourceColumns(insightViewDefinitionId?: string) {
  return useQuery<AxiosResponse<KpiDefinitionSourceColumn[]>, AxiosDefaultErrorEntity>(
    [KPI_DEFINITION_QUERY_KEY.SOURCE_COLUMNS, insightViewDefinitionId],
    () =>
      axios
        .get(`${KPI_DEFINITION_BASE_PATH}/source-columns`, {
          params: { insightViewDefinitionId },
        })
        .then((res) => res),
    { enabled: Boolean(insightViewDefinitionId) }
  );
}

export function useGetKpiInsightViewColumns(insightViewDefinitionId?: string) {
  return useQuery<AxiosResponse<KpiInsightViewColumn[]>, AxiosDefaultErrorEntity>(
    [KPI_DEFINITION_QUERY_KEY.INSIGHT_VIEW_COLUMNS, insightViewDefinitionId],
    () =>
      axios
        .get(`${KPI_DEFINITION_BASE_PATH}/insight-view-columns`, {
          params: { insightViewDefinitionId },
        })
        .then((res) => res),
    { enabled: Boolean(insightViewDefinitionId) }
  );
}

export function useCreateKpiDefinition() {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<KpiDefinition>, AxiosDefaultErrorEntity, KpiDefinitionRequest>(
    (data) => axios.post(KPI_DEFINITION_BASE_PATH, data).then((res) => res),
    {
      onSuccess: () => queryClient.invalidateQueries(KPI_DEFINITION_QUERY_KEY.LIST),
    }
  );
}

export function useUpdateKpiDefinition() {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<KpiDefinition>, AxiosDefaultErrorEntity, UpdateKpiDefinitionRequest>(
    ({ id, data }) => axios.put(`${KPI_DEFINITION_BASE_PATH}/${id}`, data).then((res) => res),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(KPI_DEFINITION_QUERY_KEY.LIST);
        queryClient.invalidateQueries(KPI_DEFINITION_QUERY_KEY.DETAILS);
      },
    }
  );
}

export function useDeleteKpiDefinition() {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<void>, AxiosDefaultErrorEntity, string>(
    (id) => axios.delete(`${KPI_DEFINITION_BASE_PATH}/${id}`).then((res) => res),
    {
      onSuccess: () => queryClient.invalidateQueries(KPI_DEFINITION_QUERY_KEY.LIST),
    }
  );
}
